import { request } from 'graphql-request'
import { mutate } from 'swr'
import useSWRInfinite from 'swr/infinite'
import { CONTENTFUL_API_URL } from './api'
import { IContentfulRichText } from './contentful.types'

const PREFIX = '@contentful/getArticleList'

interface IQuery {
  search?: string
  categoryId?: string
  featured?: boolean
  page?: number
  itemsPerPage?: number
  rss?: boolean
}

export interface IArticleListItem {
  title?: string
  coverImage?: {
    title: string
    url: string
  }
  content: IContentfulRichText
  categoriesCollection?: {
    items?: [
      {
        sys: {
          id: string
        }
        name: string
      }
    ]
  }
  slug: string
  sys: {
    id: string
    firstPublishedAt: string
  }
}

function encodeKey(payload: IQuery | undefined) {
  return `${PREFIX}${JSON.stringify({
    search: payload?.search?.trim?.() || undefined,
    categoryId: payload?.categoryId?.trim?.() || undefined,
    featured: payload?.featured != null ? Boolean(payload.featured) : undefined,
    rss: payload?.rss != null ? Boolean(payload.rss) : undefined,
    page: payload?.page || 0,
    itemsPerPage: payload?.itemsPerPage || cardsPerPage,
  })}`
}

function decodeKey(output: string): IQuery {
  let result = output

  if (result.indexOf(PREFIX) === 0) {
    result = result.slice(PREFIX.length)
  }

  return JSON.parse(result)
}

const cardsPerPage = 12

async function articleListFetcher(key: string): Promise<IArticleListItem[]> {
  const { categoryId, search, featured, rss, page, itemsPerPage = cardsPerPage } = decodeKey(key)
  const skip = (page || 0) * itemsPerPage

  // eslint-disable-next-line @typescript-eslint/ban-types
  const where: { [key: string]: string | object | undefined | boolean } = {}

  if (featured != null) {
    where.featured = Boolean(featured)
  }
  if (rss != null) {
    where.rss = Boolean(rss)
  }

  if (categoryId || search) {
    where.OR = [
      categoryId && {
        category: {
          sys: {
            id: categoryId,
          },
        },
      },
      categoryId && {
        categories: {
          sys: {
            id: categoryId,
          },
        },
      },
      search && {
        title_contains: search,
      },
      search && {
        category: {
          name_contains: search,
        },
      },
      search && {
        categories: {
          name_contains: search,
        },
      },
    ].filter(Boolean)
  }

  const data = await request(
    CONTENTFUL_API_URL,
    `query getArticleList($where: ArticleFilter, $limit: Int, $skip: Int) {
      articleCollection(
        where: $where,
        order: [sys_firstPublishedAt_DESC],
        limit: $limit, 
        skip: $skip
      ) {
        items {
          title
          coverImage {
            title
            url
          }
          categoriesCollection {
            items {
              sys {
                id
              }
              name
            }
          }
          content {
            json
          }
          slug
          sys {
            id
            firstPublishedAt
          }
        }
      }
    }`,
    { where, limit: itemsPerPage, skip }
  )

  return data?.articleCollection?.items
}

export async function getArticleList(query?: IQuery) {
  return articleListFetcher(encodeKey(query))
}

export function useArticleList(query?: IQuery, fallbackData?: IArticleListItem[]) {
  return useSWRInfinite<IArticleListItem[]>(
    (currentPage, prevData) => {
      const noNextDataPossible = prevData && !prevData.length

      if (noNextDataPossible) {
        return null
      }

      return encodeKey({ ...query, page: currentPage })
    },
    articleListFetcher,
    {
      fallbackData: fallbackData ? [fallbackData] : [],
    }
  )
}

export function mutateArticleList(payload: Promise<IArticleListItem[]>, query?: IQuery) {
  return mutate(encodeKey(query), payload)
}
